import { api, throwApiError } from "../../../api/api";
import type { PaymentAttachments, PaymentAttachmentsPayload } from "./paymentFilesApi";
import type { DpFgtsDocumentType, DpFgtsPaymentStatus } from "./dpTypes";
import type { DpGuideFinance } from "./financeBridge";

/** Guia e comprovante: só hasGuide/hasReceipt e o nome; o arquivo sai por /dp/<recolhimento>/:id/file. */
export type DpFgtsPayment = PaymentAttachments & {
  id: string;
  employerId: string;
  year: number;
  month: number;
  /** Valores em reais como string ("18421.70"), já com duas casas. */
  fgtsAmount: string;
  consignadoAmount: string;
  totalAmount: string;
  paidAt: string | null;
  /** Derivado pelo backend: dia 20 do mês seguinte à competência. */
  dueDate: string;
  status: DpFgtsPaymentStatus;
  documentType: DpFgtsDocumentType;
  notes?: string | null;
  /** Título de previsão no Financeiro (ADR 0029); null = sem título. */
  financeiro: DpGuideFinance;
  /** Ex.: "Pago em DD/MM/AAAA no DP, sem baixa no Financeiro." */
  financeiroWarning: string | null;
};

export type DpFgtsPaymentList = {
  items: DpFgtsPayment[];
  totals: { fgts: string; consignado: string; total: string };
};

export type CreateFgtsPaymentPayload = PaymentAttachmentsPayload & {
  employerId: string;
  year: number;
  month: number;
  fgtsAmount: number;
  consignadoAmount?: number;
  paidAt?: string | null;
  documentType: DpFgtsDocumentType;
  notes?: string | null;
};

/** A competência (empregador, ano e mês) não muda: lançamento errado se exclui e relança. */
export type UpdateFgtsPaymentPayload = Partial<
  Omit<CreateFgtsPaymentPayload, "employerId" | "year" | "month">
>;

const auth = (token: string) => {
  const raw = token?.startsWith("Bearer ") ? token.slice(7) : token;
  return { headers: { Authorization: `Bearer ${raw}` } };
};

export const listFgtsPaymentsApi = async (
  token: string,
  params: { employerId?: string; year?: number }
): Promise<DpFgtsPaymentList> => {
  try {
    const { data } = await api.get<DpFgtsPaymentList>("/dp/fgts-payments", {
      ...auth(token),
      params,
    });
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao listar recolhimentos.");
    throw error;
  }
};

export const createFgtsPaymentApi = async (
  token: string,
  payload: CreateFgtsPaymentPayload
): Promise<DpFgtsPayment> => {
  try {
    const { data } = await api.post<DpFgtsPayment>("/dp/fgts-payments", payload, auth(token));
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao lançar competência.");
    throw error;
  }
};

export const updateFgtsPaymentApi = async (
  token: string,
  id: string,
  payload: UpdateFgtsPaymentPayload
): Promise<DpFgtsPayment> => {
  try {
    const { data } = await api.patch<DpFgtsPayment>(`/dp/fgts-payments/${id}`, payload, auth(token));
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao editar recolhimento.");
    throw error;
  }
};

export const deleteFgtsPaymentApi = async (token: string, id: string): Promise<void> => {
  try {
    await api.delete(`/dp/fgts-payments/${id}`, auth(token));
  } catch (error) {
    throwApiError(error, "Erro ao excluir recolhimento.");
    throw error;
  }
};
